import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import { SOCKET_URL, API_BASE_URL } from '../config';
import Navigation from '../components/Navigation';
import LiveGamesSidebar from '../components/LiveGamesSidebar';
import { teams } from '../data/teams';

interface PlayerStats {
  id: string;
  name: string;
  mechClass: string;
  teamId: string;
  gamesPlayed: number;
  totalPoints: number;
  totalAssists: number;
  totalRebounds: number;
  avgPoints: number;
  avgAssists: number;
  avgRebounds: number;
  rating: number;
}

type SortField = 'name' | 'team' | 'rating' | 'totalPoints' | 'totalAssists' | 'totalRebounds' | 'avgPoints' | 'avgAssists' | 'avgRebounds';
type SortDirection = 'asc' | 'desc';

const LeagueLeadersPage: React.FC = () => {
  const [allPlayers, setAllPlayers] = useState<PlayerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('totalPoints');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    fetchAllPlayers();
    
    // Set up socket connection for live updates
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      // Connected to rosters update server
    });

    newSocket.on('disconnect', () => {
      // Disconnected from rosters update server
    });

    // Refresh rosters when games complete
    newSocket.on('continuous-game-complete', () => {
      fetchAllPlayers();
    });

    // Also refresh on game updates (for live stat tracking)
    newSocket.on('continuous-game-update', () => {
      // Debounce: only refresh every 5 seconds max
      const now = Date.now();
      if (!(window as any).lastLeadersUpdate || now - (window as any).lastLeadersUpdate > 5000) {
        (window as any).lastLeadersUpdate = now;
        fetchAllPlayers();
      }
    });

    return () => {
      newSocket.close();
    };
  }, []);

  const fetchAllPlayers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rosters`);
      const data = await response.json();
      if (data.success) {
        const rosters = data.rosters || {};
        // Flatten all players from all teams into a single array
        const players: PlayerStats[] = [];
        Object.keys(rosters).forEach(teamId => {
          const teamPlayers = rosters[teamId] || [];
          players.push(...teamPlayers);
        });
        setAllPlayers(players);
      } else {
        console.error('API returned success: false', data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rosters:', error);
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending for stats, ascending for name/team
      setSortField(field);
      if (field === 'name' || field === 'team') {
        setSortDirection('asc');
      } else {
        setSortDirection('desc');
      }
    }
  };

  const getTeamName = (teamId: string): string => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : teamId;
  };

  const getTeamAbbreviation = (teamId: string): string => {
    const team = teams.find(t => t.id === teamId);
    return team ? (team.abbreviation || team.name) : teamId;
  };

  const getTeamColor = (teamId: string): string => {
    const team = teams.find(t => t.id === teamId);
    return team ? (team.color || '#666') : '#666';
  };

  const sortedPlayers = [...allPlayers].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'team':
        aValue = getTeamAbbreviation(a.teamId).toLowerCase();
        bValue = getTeamAbbreviation(b.teamId).toLowerCase();
        break;
      case 'rating':
        aValue = a.rating;
        bValue = b.rating;
        break;
      case 'totalPoints':
        aValue = a.totalPoints;
        bValue = b.totalPoints;
        break;
      case 'totalAssists':
        aValue = a.totalAssists;
        bValue = b.totalAssists;
        break;
      case 'totalRebounds':
        aValue = a.totalRebounds;
        bValue = b.totalRebounds;
        break;
      case 'avgPoints':
        aValue = a.gamesPlayed > 0 ? a.avgPoints : 0;
        bValue = b.gamesPlayed > 0 ? b.avgPoints : 0;
        break;
      case 'avgAssists':
        aValue = a.gamesPlayed > 0 ? a.avgAssists : 0;
        bValue = b.gamesPlayed > 0 ? b.avgAssists : 0;
        break;
      case 'avgRebounds':
        aValue = a.gamesPlayed > 0 ? a.avgRebounds : 0;
        bValue = b.gamesPlayed > 0 ? b.avgRebounds : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });


  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container--interior">
          <LiveGamesSidebar />
          <div className="interior-wrapper">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '16rem' }}>
              <div style={{ color: '#22d3ee' }}>
                <div style={{ animation: 'pulse 2s infinite' }}>Loading league leaders...</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      
      <div className="container--interior">
        <LiveGamesSidebar />
        
        <div className="interior-wrapper">
          <div style={{ marginBottom: '2rem', paddingTop: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem', textTransform: 'uppercase' }}>LEAGUE LEADERS</h1>
            <Link 
              to="/rosters" 
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #fff',
                borderRadius: '4px',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              Rosters →
            </Link>
          </div>

          <div style={{ marginBottom: '3rem', overflow: 'scroll' }}>
            <div className="table--team rosters">
              {/* Table Header */}
              <div className="trow" style={{ 
                display: 'flex', 
                gap: '0.75rem', 
                padding: '0.5rem 0',
                borderBottom: '2px solid rgba(255, 255, 255, 0.3)',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <div 
                  style={{ 
                    flex: '1.5', 
                    textAlign: 'left',
                    color: sortField === 'name' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('name')}
                >
                  Name
                </div>
                <div 
                  style={{ 
                    flex: '1.5', 
                    textAlign: 'left',
                    color: sortField === 'team' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('team')}
                >
                  Team
                </div>
                <div 
                  style={{ 
                    flex: '0.8', 
                    textAlign: 'right',
                    color: sortField === 'rating' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('rating')}
                >
                  Rating
                </div>
                <div 
                  style={{ 
                    flex: '0.8', 
                    textAlign: 'right',
                    color: sortField === 'totalPoints' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('totalPoints')}
                >
                  PTS
                </div>
                <div 
                  style={{ 
                    flex: '0.8', 
                    textAlign: 'right',
                    color: sortField === 'totalAssists' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('totalAssists')}
                >
                  AST
                </div>
                <div 
                  style={{ 
                    flex: '0.8', 
                    textAlign: 'right',
                    color: sortField === 'totalRebounds' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('totalRebounds')}
                >
                  REB
                </div>
                <div 
                  style={{ 
                    flex: '0.9', 
                    textAlign: 'right',
                    color: sortField === 'avgPoints' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('avgPoints')}
                >
                  AVG PTS
                </div>
                <div 
                  style={{ 
                    flex: '0.9', 
                    textAlign: 'right',
                    color: sortField === 'avgAssists' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('avgAssists')}
                >
                  AVG AST
                </div>
                <div 
                  style={{ 
                    flex: '0.9', 
                    textAlign: 'right',
                    color: sortField === 'avgRebounds' ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'
                  }}
                  onClick={() => handleSort('avgRebounds')}
                >
                  AVG REB
                </div>
              </div>

              {/* Player Rows */}
              {sortedPlayers.map((player, index) => (
                <Link
                  key={player.id || `player-${index}`}
                  to={`/rosters?team=${player.teamId}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <div className="trow player w-inline-block" style={{ cursor: 'pointer' }}>
                    <div className="player-name" style={{ flex: '1.5', textAlign: 'left' }}>{player.name}</div>
                    <div style={{ flex: '1.5', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div 
                        className="team-dot"
                        style={{
                          backgroundColor: getTeamColor(player.teamId),
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      ></div>
                      <div>{getTeamAbbreviation(player.teamId)}</div>
                    </div>
                    <div style={{ flex: '0.8', textAlign: 'right', fontWeight: 'bold' }}>
                      {player.rating}
                    </div>
                    <div style={{ flex: '0.8', textAlign: 'right' }}>
                      {player.totalPoints !== undefined && player.totalPoints !== null ? player.totalPoints : '-'}
                    </div>
                    <div style={{ flex: '0.8', textAlign: 'right' }}>
                      {player.totalAssists !== undefined && player.totalAssists !== null ? player.totalAssists : '-'}
                    </div>
                    <div style={{ flex: '0.8', textAlign: 'right' }}>
                      {player.totalRebounds !== undefined && player.totalRebounds !== null ? player.totalRebounds : '-'}
                    </div>
                    <div style={{ flex: '0.9', textAlign: 'right' }}>
                      {player.gamesPlayed > 0 && player.avgPoints !== undefined && player.avgPoints !== null ? player.avgPoints.toFixed(1) : '-'}
                    </div>
                    <div style={{ flex: '0.9', textAlign: 'right' }}>
                      {player.gamesPlayed > 0 && player.avgAssists !== undefined && player.avgAssists !== null ? player.avgAssists.toFixed(1) : '-'}
                    </div>
                    <div style={{ flex: '0.9', textAlign: 'right' }}>
                      {player.gamesPlayed > 0 && player.avgRebounds !== undefined && player.avgRebounds !== null ? player.avgRebounds.toFixed(1) : '-'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          
          <p className="copyright"><span className="copyright-icon">© </span>2077 Neo Vegas CORP Trust</p>
        </div>
      </div>
    </>
  );
};

export default LeagueLeadersPage;

